class BoutiqueGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.track = this.querySelector('[data-gb-track]');
    this.previous = this.querySelector('[data-gb-previous]');
    this.next = this.querySelector('[data-gb-next]');
    if (!this.track || !this.previous || !this.next) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const step = () => this.track.children.length > 1 ? this.track.children[1].offsetLeft - this.track.children[0].offsetLeft : this.track.clientWidth;
    const move = (direction) => this.track.scrollBy({left: step() * direction, behavior: reduced.matches ? 'instant' : 'smooth'});
    this.track.addEventListener('keydown', (event) => {
      if (this.hasAttribute('data-native-scroll') && (event.key === 'Home' || event.key === 'End')) {
        event.preventDefault();
        this.track.scrollTo({left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: reduced.matches ? 'instant' : 'smooth'});
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
    }, {signal});
    this.previous.addEventListener('click', () => move(-1), {signal});
    this.next.addEventListener('click', () => move(1), {signal});
    this.update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft < 4;
      this.next.disabled = this.track.scrollLeft > max - 4;
      const index = this.track.clientWidth > 0 ? Math.round(this.track.scrollLeft / step()) : 0;
      this.querySelectorAll('[data-gb-dot]').forEach((dot, i) => dot.classList.toggle('gb-dot-current', i === index));
    };
    this.track.addEventListener('scroll', this.update, {passive:true, signal});
    let startX = 0;
    let startY = 0;
    let lastDelta = 0;
    let pointerId;
    let startScroll = 0;
    let dragging = false;
    let active = false;
    let suppressUntil = 0;
    this.track.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) return;
      if (this.hasAttribute('data-native-scroll') && event.pointerType !== 'mouse') return;
      startX = event.clientX;
      startY = event.clientY;
      lastDelta = 0;
      pointerId = event.pointerId;
      startScroll = this.track.scrollLeft;
      active = true;
      dragging = false;
    }, {signal});
    this.track.addEventListener('pointermove', (event) => {
      if (!active) return;
      const delta = event.clientX - startX;
      lastDelta = delta;
      if (!dragging && Math.abs(event.clientY - startY) > Math.abs(delta) && Math.abs(event.clientY - startY) > 8) { active = false; return; }
      if (Math.abs(delta) > 8 && !dragging) {
        dragging = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('gb-is-dragging');
      }
      if (dragging) {
        event.preventDefault();
        this.track.scrollLeft = startScroll - delta;
      }
    }, {signal});
    const finish = () => {
      if (!active) return;
      const wasDragging = dragging;
      const distance = step();
      let index = Math.round(this.track.scrollLeft / distance);
      if (wasDragging && Math.abs(lastDelta) > 28 && Math.abs(lastDelta) < distance / 2) index = Math.round(startScroll / distance) + (lastDelta < 0 ? 1 : -1);
      if (wasDragging) suppressUntil = Date.now() + (this.hasAttribute('data-native-scroll') ? 180 : 500);
      if (this.track.hasPointerCapture(pointerId)) this.track.releasePointerCapture(pointerId);
      active = false;
      dragging = false;
      this.track.classList.remove('gb-is-dragging');
      if (wasDragging) this.track.scrollTo({left: Math.max(0, index) * distance, behavior: reduced.matches ? 'instant' : 'smooth'});
      this.update();
    };
    this.track.addEventListener('pointerup', finish, {signal});
    this.track.addEventListener('pointercancel', finish, {signal});
    this.track.addEventListener('pointerleave', () => { if (!dragging) active = false; }, {signal});
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), {signal});
    this.track.addEventListener('click', (event) => {
      if (Date.now() < suppressUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, {capture:true, signal});
    const selectMedia = (id) => {
      if (!id) return;
      const slides = Array.from(this.track.children);
      const index = slides.findIndex((slide) => slide.dataset.sourceMedia === String(id));
      if (index >= 0) this.track.scrollTo({left:index * this.track.clientWidth, behavior:'instant'});
    };
    this.closest('.shopify-section')?.addEventListener('variant:update', (event) => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      selectMedia(event.detail?.resource?.featured_media?.id);
    }, {signal});
    let initialMediaSelected = false;
    this.resizeObserver = new ResizeObserver(() => {
      if (!initialMediaSelected && this.track.clientWidth > 0) {
        initialMediaSelected = true;
        selectMedia(this.dataset.initialMedia);
      }
      this.update();
    });
    this.resizeObserver.observe(this.track);
    this.update();
    this.classList.add('gb-gallery--ready');
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }
}
if (!customElements.get('gb-gallery')) customElements.define('gb-gallery', BoutiqueGallery);

const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
let revealObserver;
function revealSections(root = document) {
  if (motion.matches || !('IntersectionObserver' in window)) return;
  revealObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.remove('gb-reveal-pending');
      revealObserver.unobserve(entry.target);
    }
  }, {threshold:0.06});
  for (const element of root.querySelectorAll('.gb-taste,.gb-path,.gb-product,.gb-flavour__inner,.gb-about__inner,.gm-prepare,.gm-before')) {
    if (element.getBoundingClientRect().top < window.innerHeight) continue;
    if (element.closest('.gm-product-rail')) continue;
    if (element.classList.contains('gb-taste')) element.style.setProperty('--gb-delay', `${Math.min(Array.from(element.parentElement.children).indexOf(element), 3) * 70}ms`);
    element.classList.add('gb-reveal-pending');
    revealObserver.observe(element);
  }
}
revealSections();
document.addEventListener('shopify:section:load', (event) => revealSections(event.target));
motion.addEventListener('change', () => {
  if (!motion.matches) return;
  revealObserver?.disconnect();
  document.querySelectorAll('.gb-reveal-pending').forEach((element) => element.classList.remove('gb-reveal-pending'));
});

class GrunmomentNewsletter extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const signal = this.abort.signal;
    if (document.body.classList.contains('gm-email-confirmation-page')) return;
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    const key = 'gm_newsletter_dismissed_until';
    const remember = () => { try { localStorage.setItem(key, String(Date.now() + 7 * 86400000)); } catch {} };
    const open = () => { if (!dialog.open && !document.querySelector('dialog[open]')) { dialog.showModal(); } };
    if (this.querySelector('[data-news-result]')) open();
    dialog.querySelector('form')?.addEventListener('submit', () => { dialog.close(); }, {signal, capture: true});
    document.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('[data-news-open]')) open();
    }, {signal});
    dialog.querySelectorAll('[data-news-close]').forEach(button => button.addEventListener('click', () => { remember(); dialog.close(); }, {signal}));
    dialog.addEventListener('cancel', remember, {signal});
    dialog.addEventListener('click', event => { if (event.target !== dialog) return; const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) { remember(); dialog.close(); } }, {signal});
    let dismissed = false; try { dismissed = Number(localStorage.getItem(key)) > Date.now(); } catch {}
    if (!dismissed && !window.Shopify?.designMode && !/\/challenge\/?$/.test(window.location.pathname)) this.timer = setTimeout(open, Number(this.dataset.delay) || 5000);
  }
  disconnectedCallback() { clearTimeout(this.timer); this.abort?.abort(); }
}
if (!customElements.get('gm-newsletter-popup')) customElements.define('gm-newsletter-popup', GrunmomentNewsletter);
